import React, { useRef, useEffect, useState } from 'react';

/**
 * PhoneSidebar — Persistent Planchette softphone embed.
 * 
 * Fetches a bridge token from Virgo, passes to Planchette iframe
 * for cross-origin authentication.
 */
const PLANCHETTE_URL = 'https://astraea.planchetteonline.com';

export default function PhoneSidebar({ isOpen, onClose, initialCall }) {
  const iframeRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState(null);

  // Fetch bridge token when opening
  useEffect(() => {
    if (!isOpen) {
      setIframeUrl(null);
      setLoaded(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function fetchToken() {
      try {
        const res = await fetch('/api/phone-token/', { credentials: 'include' });
        if (!res.ok) {
          setError('Auth failed: ' + res.status);
          return;
        }
        const data = await res.json();
        if (cancelled) return;

        let url = `${PLANCHETTE_URL}/dialer?embed=true&virgo_token=${data.token}`;
        if (initialCall?.number) {
          url += `&number=${encodeURIComponent(initialCall.number)}&autodial=true`;
        }
        if (initialCall?.name) {
          url += `&name=${encodeURIComponent(initialCall.name)}`;
        }
        setIframeUrl(url);
      } catch (err) {
        if (!cancelled) setError('Failed to get phone token');
      }
    }

    fetchToken();
    return () => { cancelled = true; };
  }, [isOpen, initialCall]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-[9998]" 
          onClick={onClose}
        />
      )}
      
      {/* Sidebar panel */}
      <div
        className={`fixed top-0 right-0 h-full z-[9999] transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: 380, backgroundColor: '#0e0e1a' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1a1a2e]">
          <span 
            className="text-xs tracking-[0.2em] text-[#00e5ff] font-medium"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            ASTRAEA PHONE
          </span>
          <button
            onClick={onClose}
            className="text-[#64748b] hover:text-white text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        {isOpen && iframeUrl && (
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            className="w-full border-0"
            style={{ height: 'calc(100vh - 48px)' }}
            allow="microphone; camera; autoplay"
            onLoad={() => setLoaded(true)}
          />
        )}

        {/* Loading state */}
        {isOpen && !iframeUrl && !error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-[#00e5ff] text-sm font-mono animate-pulse">
              Connecting to Planchette...
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-400 text-sm font-mono">{error}</div>
          </div>
        )}
      </div>
    </>
  );
}
